/**
 * Curriculum Phase Rollout Configuration
 *
 * Controls which courses are live, locked, or coming soon.
 * To unlock a course, simply change its status to "live".
 */

export type CourseStatus = "live" | "coming_soon" | "in_development" | "planned" | "next_release";

export interface CoursePhaseConfig {
  status: CourseStatus;
  description: string;
  badge: string;
}

const LIVE_COURSES = new Set([
  "Financial Foundations",
]);

const COURSE_DESCRIPTIONS: Record<string, string> = {
  // Tier 1
  "Financial Confidence System": "Build the mindset and confidence foundation for your financial journey.",
  "Financial Foundations": "Master the essential building blocks of personal finance — from income to net worth.",
  "Banking & Cash Management": "Learn how banking systems work and how to manage cash flow effectively.",
  "Saving & Budgeting": "Develop disciplined saving habits and create budgets that actually work.",
  "Credit & Debt Management": "Understand credit scores, manage debt strategically, and build financial leverage.",
  // Tier 2
  "Behavioral Finance": "Discover the psychology behind financial decisions and overcome cognitive biases.",
  "Career & Income Strategy": "Build career capital, negotiate compensation, and create multiple income streams.",
  "Insurance & Risk Protection": "Protect your wealth with strategic insurance and risk management.",
  "Taxes": "Navigate the tax system and make tax-efficient financial decisions.",
  "Transportation & Vehicle Ownership": "Make smart vehicle decisions — from purchase to total cost of ownership.",
  "Family Finance": "Plan for dependents, manage household budgets, and protect your family's future.",
  "Financial Scams & Fraud Awareness": "Identify financial scams, protect your identity, and recognize manipulation.",
  "Financial Recovery": "Rebuild after financial setbacks with proven stabilization strategies.",
  // Tier 3
  "Economic Foundations": "Understand how economies work — scarcity, tradeoffs, and market fundamentals.",
  "Microeconomics": "Explore supply, demand, pricing, and individual market behavior.",
  "Market Structures & Competition": "Analyze monopolies, oligopolies, and competitive market dynamics.",
  "Labor Economics": "Understand wage determination, specialization, and labor market forces.",
  "Macroeconomic Foundations": "Study GDP, inflation, unemployment, and economic cycles.",
  "Money, Banking & Central Banking": "Learn how money supply, banking reserves, and central bank policy shape the economy.",
  "Fiscal Policy & Government Economics": "Explore taxation, government spending, and policy tradeoffs.",
  "International Economics": "Understand global trade, exchange rates, and international financial systems.",
  "Behavioral Economics": "Study how real human behavior deviates from rational economic models.",
  "Economic Development & Institutions": "Explore how institutions and policy drive national economic growth.",
  "Game Theory & Strategic Interaction": "Master strategic decision-making, signaling, and competitive dynamics.",
  "Economic History": "Learn from major economic events, crises, and transformations throughout history.",
  "Economic Systems in Practice": "Compare capitalism, socialism, and mixed economies in real-world application.",
  // Tier 4
  "Investing Fundamentals": "Build a foundation in stocks, bonds, diversification, and risk management.",
  "Retirement & Investment Accounts": "Navigate IRAs, 401(k)s, and long-term investment account strategies.",
  "Real Estate (Homeownership)": "Walk through buying a home — affordability, mortgages, and building equity.",
  "Real Estate Investing": "Evaluate rental properties, analyze deals, and build a real estate portfolio.",
  "Advanced Financial Strategy": "Combine advanced tactics for tax optimization, asset allocation, and wealth preservation.",
  // Tier 5
  "Entrepreneurial Foundations": "Develop the entrepreneurial mindset and understand what it takes to build a business.",
  "Opportunity Identification": "Learn to spot viable business opportunities and validate market demand.",
  "Offer Creation & Validation": "Create compelling offers and test them before going to market.",
  "Sales & Marketing Fundamentals": "Master trust-based selling and customer acquisition strategies.",
  "Business Model Design": "Build sustainable business models with pricing, costs, and retention strategies.",
  "Operations & Execution": "Design efficient operations, fulfillment systems, and quality control processes.",
  "Entrepreneurial Finance": "Manage burn rate, runway, cash flow, and reinvestment as a founder.",
  "Growth & Scale": "Scale your business through hiring, systems, and channel expansion.",
  "Founder Decision-Making": "Navigate high-stakes decisions under pressure, uncertainty, and time constraints.",
  "Ownership, Equity & Exit Strategy": "Understand dilution, equity structures, control dynamics, and exit planning.",
  "Business Fundamentals": "Learn the core principles of running a successful business operation.",
  "Startups & Entrepreneurship": "Explore the startup lifecycle from idea to product-market fit.",
  "Business & Startup Expansion": "Take your business from traction to sustainable growth and market expansion.",
  // Tier 6
  "Real Estate Development": "Manage development projects from land acquisition to ROI analysis.",
};

const STATUS_BADGES: Record<CourseStatus, string> = {
  live: "Live",
  coming_soon: "Coming Soon",
  next_release: "Next Release",
  in_development: "In Development",
  planned: "Planned",
};

// Assign statuses based on tier proximity
const NEXT_RELEASE_COURSES = new Set([
  "Banking & Cash Management",
  "Saving & Budgeting",
  "Credit & Debt Management",
]);

const COMING_SOON_COURSES = new Set([
  "Behavioral Finance",
  "Career & Income Strategy",
  "Insurance & Risk Protection",
  "Taxes",
  "Transportation & Vehicle Ownership",
  "Family Finance",
  "Financial Scams & Fraud Awareness",
  "Financial Recovery",
]);

const IN_DEVELOPMENT_COURSES = new Set([
  "Economic Foundations",
  "Microeconomics",
  "Market Structures & Competition",
  "Labor Economics",
  "Macroeconomic Foundations",
  "Money, Banking & Central Banking",
  "Fiscal Policy & Government Economics",
  "International Economics",
  "Behavioral Economics",
  "Economic Development & Institutions",
  "Game Theory & Strategic Interaction",
  "Economic History",
  "Economic Systems in Practice",
  "Investing Fundamentals",
  "Retirement & Investment Accounts",
  "Real Estate (Homeownership)",
  "Real Estate Investing",
  "Advanced Financial Strategy",
]);

export function getCoursePhaseConfig(courseName: string): CoursePhaseConfig {
  const isLive = LIVE_COURSES.has(courseName);

  let status: CourseStatus;
  if (isLive) {
    status = "live";
  } else if (NEXT_RELEASE_COURSES.has(courseName)) {
    status = "next_release";
  } else if (COMING_SOON_COURSES.has(courseName)) {
    status = "coming_soon";
  } else if (IN_DEVELOPMENT_COURSES.has(courseName)) {
    status = "in_development";
  } else {
    status = "planned";
  }

  return {
    status,
    description: COURSE_DESCRIPTIONS[courseName] || "A premium learning path coming to HeirWise.",
    badge: STATUS_BADGES[status],
  };
}

export function isCourseAccessible(courseName: string): boolean {
  return LIVE_COURSES.has(courseName);
}

export function getStatusBadgeColor(status: CourseStatus): string {
  switch (status) {
    case "live":
      return "bg-emerald-500/15 text-emerald-400 border-emerald-500/30";
    case "next_release":
      return "bg-primary/15 text-primary border-primary/30";
    case "coming_soon":
      return "bg-accent/15 text-accent border-accent/30";
    case "in_development":
      return "bg-secondary/15 text-secondary border-secondary/30";
    case "planned":
      return "bg-muted text-muted-foreground border-border";
  }
}
